import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Clock, Shield, UserCheck, UserX, AlertCircle, Users, Lock } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

type GameDayStatus = 'present_at_venue' | 'starting_lineup' | 'on_bench' | 'no_show' | 'substitute_in' | 'disqualified' | 'ejected';

interface RosterPlayer {
  id: string;
  rosterId: string;
  name: string;
  username?: string;
  gameDayStatus: GameDayStatus | null;
  playerType: string;
}

interface GameDayReadinessProps {
  gameId: string;
  teamSide: 'home' | 'away';
  teamName: string;
  gameDate?: string;
  gameTime?: string;
  canEdit: boolean;
}

const STATUS_CONFIG: Record<GameDayStatus, { label: string; icon: typeof UserCheck; color: string }> = {
  present_at_venue: { label: 'Present at Venue', icon: UserCheck, color: 'bg-emerald-600 text-white' },
  starting_lineup: { label: 'Starting Lineup', icon: Shield, color: 'bg-purple-600 text-white' },
  on_bench: { label: 'On Bench', icon: Users, color: 'bg-blue-600 text-white' },
  no_show: { label: 'No Show', icon: UserX, color: 'bg-gray-600 text-white' },
  substitute_in: { label: 'Substitute In', icon: Users, color: 'bg-blue-500 text-white' },
  disqualified: { label: 'Disqualified', icon: AlertCircle, color: 'bg-red-700 text-white' },
  ejected: { label: 'Ejected', icon: AlertCircle, color: 'bg-red-800 text-white' },
};

export const GameDayReadiness: React.FC<GameDayReadinessProps> = ({
  gameId,
  teamSide,
  teamName,
  gameDate,
  gameTime,
  canEdit
}) => {
  const [players, setPlayers] = useState<RosterPlayer[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // Check if we're within 1 hour of game time
  const isWithinGameWindow = useMemo(() => {
    if (!gameDate) return false;
    try {
      const gameDateTime = new Date(`${gameDate}T${gameTime || '00:00'}`);
      const now = new Date();
      const oneHourBefore = new Date(gameDateTime.getTime() - 60 * 60 * 1000);
      return now >= oneHourBefore;
    } catch {
      return false;
    }
  }, [gameDate, gameTime]);

  useEffect(() => {
    loadRosterPlayers();
  }, [gameId, teamSide]);

  const loadRosterPlayers = async () => {
    try {
      setLoading(true);
      const { data: rosterData, error } = await supabase
        .from('game_rosters_live')
        .select('id, player_id, player_type, game_day_status')
        .eq('game_id', gameId)
        .eq('team_side', teamSide);

      if (error) throw error;

      const playersList: RosterPlayer[] = [];

      for (const record of rosterData || []) {
        let playerInfo = null;
        if (record.player_type === 'supabase') {
          const { data } = await supabase.from('profiles').select('username').eq('id', record.player_id).maybeSingle();
          playerInfo = data;
        } else {
          const { data } = await supabase.from('internal_users').select('username').eq('id', record.player_id).maybeSingle();
          playerInfo = data;
        }

        playersList.push({
          id: record.player_id,
          rosterId: record.id,
          name: playerInfo?.username ? `@${playerInfo.username}` : 'Unknown Player',
          username: playerInfo?.username,
          gameDayStatus: record.game_day_status as GameDayStatus | null,
          playerType: record.player_type || 'supabase',
        });
      }

      setPlayers(playersList);
    } catch (error) {
      console.error('Error loading game day readiness:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (rosterId: string, status: GameDayStatus) => {
    try {
      const { error } = await supabase
        .from('game_rosters_live')
        .update({ game_day_status: status })
        .eq('id', rosterId);

      if (error) throw error;

      setPlayers(prev => prev.map(p =>
        p.rosterId === rosterId ? { ...p, gameDayStatus: status } : p
      ));

      const config = STATUS_CONFIG[status];
      toast({ title: 'Status Updated', description: `Player set to ${config.label}` });
    } catch (error) {
      console.error('Error updating game day status:', error);
      toast({ title: 'Error', description: 'Failed to update status', variant: 'destructive' });
    }
  };

  if (!isWithinGameWindow) {
    return (
      <Card className="opacity-75">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5" />
            Game Day Readiness - {teamName}
          </CardTitle>
          <CardDescription>
            This section unlocks 1 hour before game time. Use it to track real-time player statuses during game day.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert>
            <Clock className="h-4 w-4" />
            <AlertTitle>Not Yet Available</AlertTitle>
            <AlertDescription>
              Game day statuses (Present at Venue, On Bench, Substitute In, Ejected, etc.) become available 1 hour before the scheduled game time.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Game Day Readiness - {teamName}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">Loading players...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Game Day Readiness - {teamName}
          <Badge variant="outline" className="ml-2">
            {players.length} player{players.length !== 1 ? 's' : ''}
          </Badge>
        </CardTitle>
        <CardDescription>
          Set real-time game day status for each player. This tracks who is physically present and their in-game role.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {players.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No players in roster for this team.</p>
          </div>
        ) : (
          players.map(player => {
            const currentConfig = player.gameDayStatus ? STATUS_CONFIG[player.gameDayStatus] : null;
            const Icon = currentConfig?.icon || UserX;

            return (
              <div key={player.id} className="flex flex-col md:flex-row items-start md:items-center gap-3 p-3 border rounded-lg">
                <div className="flex-1 min-w-0">
                  <p className="font-medium">{player.name}</p>
                </div>
                <div className="flex items-center gap-2 w-full md:w-auto">
                  {currentConfig && (
                    <Badge className={`flex items-center gap-1 ${currentConfig.color}`}>
                      <Icon className="h-3 w-3" />
                      {currentConfig.label}
                    </Badge>
                  )}
                  {canEdit && (
                    <Select
                      value={player.gameDayStatus || ''}
                      onValueChange={(value) => handleStatusChange(player.rosterId, value as GameDayStatus)}
                    >
                      <SelectTrigger className="w-full md:w-44 h-10">
                        <SelectValue placeholder="Set status..." />
                      </SelectTrigger>
                      <SelectContent className="z-50 bg-background">
                        <SelectItem value="present_at_venue">Present at Venue</SelectItem>
                        <SelectItem value="starting_lineup">Starting Lineup</SelectItem>
                        <SelectItem value="on_bench">On Bench</SelectItem>
                        <SelectItem value="substitute_in">Substitute In</SelectItem>
                        <SelectItem value="no_show">No Show</SelectItem>
                        <SelectItem value="disqualified">Disqualified</SelectItem>
                        <SelectItem value="ejected">Ejected</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
};
